import { PropsWithChildren, useEffect, useRef, useState } from "react";
import classNames from "classnames/bind";
import ChevronLeft from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ChevronLeft24PxOutlined";
import ChevronRight from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ChevronRight24PxOutlined";
import { GalleryVideoElement } from "../GalleryVideoElement/GalleryVideoElement";
import styles from "./Carousel.module.scss";
import { Thumbnails } from "../Thumbnails/Thumbnails";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import { useRouter } from "next/router";
import { GalleryObject } from "@/components/Galleries/utils";
import { GalleryImageElement } from "../GalleryImageElement/GalleryImageElement";

const cx = classNames.bind(styles);

type CarouselItemProps = {
  mediaItem: GalleryObject;
  isSelected: boolean;
  index: number;
  fullscreen?: boolean;
  imageSizes?: string;
  intersectionObserver: IntersectionObserver | null;
  hasSuperZoom?: boolean;
};

type CarouselProps = {
  mediaItems: GalleryObject[];
  selectedInd: number;
  setSelectedInd: (arg: number) => void;
  fullscreen?: boolean;
  initialised?: boolean;
  imageSizes?: string;
  hasImmediateStateUpdates?: boolean;
  hasTransparentThumbnails?: boolean;
  hasDeferredMonetateQEvents?: boolean;
  hasDeferredIntersectionObserverCallback?: boolean;
  hasSuperZoom?: boolean;
};

function scrollIntoView(container: HTMLElement, target: HTMLElement) {
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const targetScrollXPos =
    target.offsetLeft - (containerRect.width - targetRect.width) / 2;
  const targetScrollYPos =
    target.offsetTop - (containerRect.height - targetRect.height) / 2;

  container.scrollTo(targetScrollXPos, targetScrollYPos);
}

const CarouselItem = ({
  mediaItem,
  index,
  fullscreen,
  imageSizes,
  intersectionObserver,
  isSelected,
  hasSuperZoom,
}: CarouselItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemRef.current && intersectionObserver) {
      intersectionObserver.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current && intersectionObserver) {
        intersectionObserver.unobserve(itemRef.current);
      }
    };
  }, [intersectionObserver]);

  if (!mediaItem) {
    return null;
  }

  return (
    <div ref={itemRef} key={mediaItem.imageUrl} className={styles.galleryItem}>
      {mediaItem.type === "video" ? (
        <GalleryVideoElement
          className={styles.galleryImage}
          key={`galleryobjectkey-${index}`}
          video={mediaItem}
          videoId={index.toString()}
          isInView={isSelected}
          isModal={fullscreen}
        />
      ) : (
        <GalleryImageElement
          fullscreen={fullscreen}
          mediaItem={mediaItem}
          isSelected={isSelected}
          index={index}
          hasSuperZoom={hasSuperZoom}
          sizes={imageSizes}
        />
      )}
    </div>
  );
};

const generateMediaItemsKey = (mediaItems: GalleryObject[]) =>
  mediaItems.map((item) => item.thumbnailUrl).join("|");

export const Carousel = ({
  mediaItems,
  selectedInd,
  setSelectedInd,
  children,
  fullscreen,
  initialised = true,
  imageSizes = "100vw",
  hasImmediateStateUpdates,
  hasTransparentThumbnails,
  hasDeferredMonetateQEvents,
  hasDeferredIntersectionObserverCallback,
  hasSuperZoom,
}: PropsWithChildren<CarouselProps>) => {
  const router = useRouter();
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenTransitionEnded, setFullscreenTransitionEnded] =
    useState(false);
  const mediaItemRef = useRef(generateMediaItemsKey(mediaItems));
  const [loading, setLoading] = useState(false);
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);
  const modalMode =
    mediaItems[0]?.type === "video" ? "videoModal" : "imageModal";

  const scrollToIndex = (index: number) => {
    if (index < 0 || index >= mediaItems.length || !mainContainerRef.current) {
      return;
    }

    scrollIntoView(
      mainContainerRef.current,
      mainContainerRef.current.children[index] as HTMLElement,
    );
  };

  const handleFullscreenTransitionEnd = () => {
    setFullscreenTransitionEnded(true);
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const intersectingEntry = entries.find(
      ({ isIntersecting }) => isIntersecting,
    );

    if (mainContainerRef.current && intersectingEntry) {
      const intersectionIndex = [...mainContainerRef.current.children].indexOf(
        intersectingEntry.target,
      );

      if (hasDeferredIntersectionObserverCallback) {
        setTimeout(() => {
          setSelectedInd(intersectionIndex);
        }, 1000);
      } else {
        setSelectedInd(intersectionIndex);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: mainContainerRef.current,
      threshold: 0.5,
    });

    setIntersectionObserver(observer);

    if (fullscreen) {
      scrollToIndex(selectedInd);

      document
        .querySelector(".ReactModalPortal > div")
        ?.addEventListener("transitionend", handleFullscreenTransitionEnd);
    }

    return () => {
      document
        .querySelector(".ReactModalPortal > div")
        ?.removeEventListener("transitionend", handleFullscreenTransitionEnd);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (thumbsContainerRef.current) {
      scrollIntoView(
        thumbsContainerRef.current,
        thumbsContainerRef.current.children[selectedInd] as HTMLElement,
      );
    }
  }, [selectedInd]);

  useEffect(() => {
    if (generateMediaItemsKey(mediaItems) !== mediaItemRef.current) {
      mainContainerRef.current?.scrollTo(0, 0);
      mediaItemRef.current = generateMediaItemsKey(mediaItems);
    }
  }, [mediaItems]);

  useEffect(() => {
    const handleRouteChange = () => {
      setLoading(true);
    };

    const handleRouteChangeComplete = () => {
      setLoading(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router]);

  const handlePrevClick = () => {
    scrollToIndex(selectedInd - 1);
    if (selectedInd > 0 && hasImmediateStateUpdates) {
      setSelectedInd(selectedInd - 1);
    }
    dispatchGalleryMonetateQEvent(
      "LeftArrow",
      fullscreen ? modalMode : "generic",
      hasDeferredMonetateQEvents,
    );
  };

  const handleNextClick = () => {
    scrollToIndex(selectedInd + 1);
    if (selectedInd < mediaItems.length - 1 && hasImmediateStateUpdates) {
      setSelectedInd(selectedInd + 1);
    }
    dispatchGalleryMonetateQEvent(
      "RightArrow",
      fullscreen ? modalMode : "generic",
      hasDeferredMonetateQEvents,
    );
  };

  return (
    <div
      className={cx(styles.wrapper, {
        "wrapper--fullscreen": fullscreen,
      })}
      data-testid={fullscreen ? "gallery:zoom:modal" : "slot:header"}
    >
      <div
        className={cx(styles.main, {
          "main--fullscreen": fullscreen,
          "main--singleItem": mediaItems.length < 2,
        })}
      >
        <div
          data-test-id="carousel-main-container"
          className={cx(styles.gallery, {
            "gallery--initialised": initialised,
            "gallery--loading":
              (fullscreen && !fullscreenTransitionEnded) || loading,
          })}
          ref={mainContainerRef}
        >
          {mediaItems.map((mediaItem, ind) => (
            <CarouselItem
              mediaItem={mediaItem}
              intersectionObserver={intersectionObserver}
              fullscreen={fullscreen}
              index={ind}
              isSelected={selectedInd === ind}
              key={`${mediaItem.imageUrl}-${ind}`}
              imageSizes={imageSizes}
              hasSuperZoom={hasSuperZoom}
            />
          ))}
        </div>
        <button
          className={styles.buttonPrev}
          type="button"
          onClick={handlePrevClick}
          data-testid="standard:gallery:navigation:left"
          disabled={selectedInd < 1}
          aria-label="Previous Image"
        >
          <ChevronLeft />
        </button>
        <button
          className={styles.buttonNext}
          type="button"
          onClick={handleNextClick}
          data-testid="standard:gallery:navigation:right"
          disabled={selectedInd >= mediaItems.length - 1}
          aria-label="Next Image"
        >
          <ChevronRight />
        </button>
        {children}
      </div>

      {mediaItems.length > 1 && (
        <Thumbnails
          media={mediaItems}
          ref={thumbsContainerRef}
          scrollToIndex={scrollToIndex}
          selectedInd={selectedInd}
          className={styles.thumbnails}
          fullscreen={fullscreen}
          transparent={hasTransparentThumbnails}
        />
      )}
    </div>
  );
};
